// ./src/hooks/useExpenseTags.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";
import {
  ExpenseTag,
  ExpenseTagService,
  ExpenseTagFilter,
  ExpenseTagInput,
  Pagination,
} from "@/utils/api";
import { SortItem } from "@/utils/api";
import { useApp } from "@/contexts";
import { ApiError } from "@/utils";
import { DEFAULT_PAGE_SIZE } from "@/config";
import { getNextTagColor } from "@/contexts/ExpenseTagsContext/tagColors";

// ============================================
// Types
// ============================================

export interface UseExpenseTagsOptions {
  /**
   * Page size for pagination.
   * Mount-time configuration — changing after mount has no effect.
   */
  pageSize?: number;

  /**
   * Filters applied on initial autoLoad and userId-change reloads.
   * Mount-time configuration — call list(newFilters) to change.
   */
  initialFilters?: Partial<ExpenseTagFilter>;

  /**
   * Sort applied on initial autoLoad and userId-change reloads.
   * Mount-time configuration — pass sort to list() to change.
   */
  initialSort?: SortItem[];

  /** Auto-load data on mount (and on userId change) */
  autoLoad?: boolean;
}

export interface UseExpenseTagsReturn {
  // Data state
  expenseTag: ExpenseTag | null;
  expenseTags: ExpenseTag[];
  loading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  // Pagination state
  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingId: string | null;
  selectedExpenseTag: ExpenseTag | null;

  // Filter and sort
  filters: Partial<ExpenseTagFilter>;
  sortBy: SortItem[];

  // Core operations
  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<ExpenseTagFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: ExpenseTagInput) => Promise<OpResult>;
  update: (id: string, params: ExpenseTagInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;

  // Convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  // UI state management
  setSelectedExpenseTag: (tag: ExpenseTag | null) => void;

  // Helper operations
  reset: () => void;

  // Lookup helpers
  getExpenseTagById: (id: string | undefined) => ExpenseTag | undefined;
  getTagsByIds: (ids: string[]) => ExpenseTag[];

  // Derived data
  activeTags: ExpenseTag[];
  totalTagsCount: number;
  activeTagsCount: number;
  hasTags: boolean;

  // Color helpers
  getNextAutoColor: () => string;

  // Inline creation (for TagsSelectorInput)
  createTagInline: (tagName: string) => Promise<ExpenseTag | null>;

  // Error handling
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_SORT: SortItem[] = [{ name: "tagName", order: "ASC" }];
const DEFAULT_FILTERS: Partial<ExpenseTagFilter> = { status: [STATUSES.ACTIVE] };

// ============================================
// Hook Implementation
// ============================================

export const useExpenseTags = (
  options: UseExpenseTagsOptions = {}
): UseExpenseTagsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = true,
  } = options;

  // Get user from AppContext
  const { user } = useApp();
  const userId = user?.id ?? null;

  // ── Mount-time configuration captured in refs ──
  const pageSizeRef = useRef(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);

  // Ref to track the last-queried filters/sort for loadMore/refresh
  const lastQueriedFilterRef = useRef<Partial<ExpenseTagFilter>>(initialFilters);
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  // Monotonically increasing call ID to detect stale list() responses
  const listCallIdRef = useRef(0);

  // Track previous userId for change detection
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Data state
  const [expenseTag, setExpenseTag] = useState<ExpenseTag | null>(null);
  const [expenseTags, setExpenseTags] = useState<ExpenseTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters — tracks what was last queried
  const [filters, setFilters] = useState<Partial<ExpenseTagFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [selectedExpenseTag, setSelectedExpenseTag] = useState<ExpenseTag | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => isLoaded && expenseTags.length === 0,
    [isLoaded, expenseTags.length]
  );

  // ==========================================
  // List Helpers
  // ==========================================

  const updateOrAddInList = useCallback((updatedItem: ExpenseTag) => {
    setExpenseTags((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === updatedItem.id);
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = updatedItem;
        return newItems;
      } else {
        return [updatedItem, ...prev];
      }
    });
  }, []);

  const removeFromList = useCallback((id: string) => {
    setExpenseTags((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const removeManyFromList = useCallback((ids: string[]) => {
    const idsSet = new Set(ids);
    setExpenseTags((prev) => prev.filter((item) => !idsSet.has(item.id || "")));
  }, []);

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const reset = useCallback(() => {
    listCallIdRef.current++;
    setExpenseTag(null);
    setExpenseTags([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingId(null);
    setSelectedExpenseTag(null);
  }, []);

  // ==========================================
  // Core Operations
  // ==========================================

  const get = useCallback(
    async (id: string): Promise<OpResult> => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await ExpenseTagService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = (result.data as ExpenseTag[])[0];
          setExpenseTag(data);
          updateOrAddInList(data);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load expense tag"));
        setExpenseTag(null);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load expense tag"));
        setExpenseTag(null);
        return result;
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  /**
   * List expense tags.
   * Race-condition safe via listCallIdRef.
   */
  const list = useCallback(
    async (
      filterOverride: Partial<ExpenseTagFilter> = {},
      pagination?: Pagination,
      sortByOverride?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setCurrentPage(1);
      }

      setLastError(null);

      // Store queried filters/sort in refs
      setFilters(filterOverride);
      lastQueriedFilterRef.current = filterOverride;
      if (sortByOverride) {
        setSortBy(sortByOverride);
        lastQueriedSortRef.current = sortByOverride;
      }

      const currentSortBy = sortByOverride || lastQueriedSortRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const result = await ExpenseTagService.list(
          filterOverride as ExpenseTagFilter,
          paginationParams,
          currentSortBy
        );

        // Guard stale responses
        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as ExpenseTag[])
            : [];

          if (isAppending) {
            setExpenseTags((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(item.id)
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            setExpenseTags(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setIsLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load expense tags"));
        setIsLoaded(true);
        if (!isAppending) setExpenseTags([]);
        return result;
      } catch (error) {
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load expense tags"));
        setIsLoaded(true);
        if (!isAppending) setExpenseTags([]);
        return result;
      } finally {
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  const getMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) return new OpResult();

      setLoading(true);
      setLastError(null);

      try {
        const result = await ExpenseTagService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedItems = Array.isArray(result.data)
            ? (result.data as ExpenseTag[])
            : [];
          loadedItems.forEach((item) => updateOrAddInList(item));
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load expense tags"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load expense tags"));
        return result;
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // ==========================================
  // Mutation Operations
  // ==========================================

  const create = useCallback(
    async (params: ExpenseTagInput): Promise<OpResult> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await ExpenseTagService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItem = (result.data as ExpenseTag[])[0];
          setExpenseTag(newItem);
          setExpenseTags((prev) => [newItem, ...prev]);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to create expense tag"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to create expense tag"));
        return result;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  const update = useCallback(
    async (id: string, params: ExpenseTagInput): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Expense tag ID is required for updating"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await ExpenseTagService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as ExpenseTag[])[0];
          setExpenseTag(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to update expense tag"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to update expense tag"));
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList]
  );

  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Expense tag ID is required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setRemovingId(id);
      setLastError(null);

      try {
        const result = await ExpenseTagService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setExpenseTag(null);
          removeFromList(id);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove expense tag"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to remove expense tag"));
        return result;
      } finally {
        setRemovingId(null);
      }
    },
    [removeFromList]
  );

  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "At least one expense tag ID is required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setLastError(null);

      try {
        const result = await ExpenseTagService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setExpenseTag(null);
          removeManyFromList(ids);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove expense tags"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to remove expense tags"));
        return result;
      }
    },
    [removeManyFromList]
  );

  // ==========================================
  // Convenience Methods
  // ==========================================

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    const nextPage = currentPage + 1;
    await list(
      lastQueriedFilterRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loadingMore, hasMore, currentPage]);

  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFilterRef.current,
      { page: 1, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ==========================================
  // Lookup Helpers
  // ==========================================

  const getExpenseTagById = useCallback(
    (id: string | undefined): ExpenseTag | undefined => {
      if (!id) return undefined;
      return expenseTags.find((item) => item.id === id);
    },
    [expenseTags]
  );

  const getTagsByIds = useCallback(
    (ids: string[]): ExpenseTag[] => {
      if (!ids || ids.length === 0) return [];
      return ids
        .map((id) => expenseTags.find((tag) => tag.id === id))
        .filter((tag): tag is ExpenseTag => tag !== undefined);
    },
    [expenseTags]
  );

  // ==========================================
  // Derived Data
  // ==========================================

  const activeTags = useMemo(
    () => expenseTags.filter((tag) => tag.status === STATUSES.ACTIVE),
    [expenseTags]
  );

  const totalTagsCount = useMemo(() => expenseTags.length, [expenseTags]);

  const activeTagsCount = useMemo(() => activeTags.length, [activeTags]);

  const hasTags = useMemo(() => activeTags.length > 0, [activeTags]);

  // ==========================================
  // Color Helpers
  // ==========================================

  const getNextAutoColor = useCallback((): string => {
    return getNextTagColor(expenseTags.length);
  }, [expenseTags.length]);

  // ==========================================
  // Inline Creation (for TagsSelectorInput)
  // ==========================================

  /**
   * Create a tag inline with auto-assigned color.
   * Checks for duplicates (case-insensitive) before creating.
   * Returns the created or existing ExpenseTag, or null on failure.
   */
  const createTagInline = useCallback(
    async (tagName: string): Promise<ExpenseTag | null> => {
      const trimmedName = tagName.trim();
      if (!trimmedName) return null;

      // Check if tag with same name already exists (case-insensitive)
      const existingTag = expenseTags.find(
        (tag) =>
          tag.tagName?.toLowerCase() === trimmedName.toLowerCase() &&
          tag.status === STATUSES.ACTIVE
      );

      if (existingTag) {
        return existingTag;
      }

      const tagColor = getNextTagColor(expenseTags.length);

      const params: ExpenseTagInput = {
        tagName: trimmedName,
        tagColor,
        status: STATUSES.ACTIVE,
      };

      const result = await create(params);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        return (result.data as ExpenseTag[])[0] || null;
      }

      return null;
    },
    [expenseTags, create]
  );

  // ==========================================
  // Effects
  // ==========================================

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }
      return;
    }

    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      expenseTag,
      expenseTags,
      loading,
      isLoaded,
      isEmpty,

      // Pagination
      loadingMore,
      hasMore,
      currentPage,
      pageSize: pageSizeRef.current,

      // Operations state
      isCreating,
      isUpdating,
      removingId,
      selectedExpenseTag,

      // Filter and sort
      filters,
      sortBy,

      // Core operations
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,

      // Convenience methods
      loadMore,
      refresh,

      // UI state management
      setSelectedExpenseTag,

      // Helper operations
      reset,

      // Lookup helpers
      getExpenseTagById,
      getTagsByIds,

      // Derived data
      activeTags,
      totalTagsCount,
      activeTagsCount,
      hasTags,

      // Color helpers
      getNextAutoColor,

      // Inline creation
      createTagInline,

      // Error handling
      lastError,
      clearError,
    }),
    [
      expenseTag,
      expenseTags,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      isCreating,
      isUpdating,
      removingId,
      selectedExpenseTag,
      filters,
      sortBy,
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,
      loadMore,
      refresh,
      reset,
      getExpenseTagById,
      getTagsByIds,
      activeTags,
      totalTagsCount,
      activeTagsCount,
      hasTags,
      getNextAutoColor,
      createTagInline,
      lastError,
      clearError,
    ]
  );
};